import { cn } from "@/lib/utils";
import { CheckCircle2, Lock, Circle, PlayCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Module } from "@/hooks/useCourses";

interface ModuleListProps {
  modules: Module[];
  currentModuleId?: string;
  onSelectModule: (moduleId: string) => void;
  isEnrolled: boolean;
  /** Map of moduleId → true if locked by cohort gating */
  cohortLockedMap?: Record<string, boolean>;
}

export function ModuleList({
  modules,
  currentModuleId,
  onSelectModule,
  isEnrolled,
  cohortLockedMap,
}: ModuleListProps) {
  // Filter out unpublished modules for learners
  const visibleModules = modules.filter((m) => m.is_published !== false);

  return (
    <div className="space-y-1">
      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide px-3 mb-3">
        Course Modules
      </h3>
      <TooltipProvider>
        {visibleModules.map((module, index) => {
          const isActive = module.id === currentModuleId;
          const isCohortLocked = cohortLockedMap?.[module.id] === true;
          const canAccess = isEnrolled && module.is_unlocked && !isCohortLocked;

          const button = (
            <button
              key={module.id}
              onClick={() => canAccess && onSelectModule(module.id)}
              disabled={!canAccess}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : canAccess
                  ? "hover:bg-muted"
                  : "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex-shrink-0">
                {module.is_completed ? (
                  <CheckCircle2
                    className={cn(
                      "h-5 w-5",
                      isActive ? "text-primary-foreground" : "text-success"
                    )}
                  />
                ) : isCohortLocked ? (
                  <Lock className="h-5 w-5 text-muted-foreground" />
                ) : !canAccess ? (
                  <Lock className="h-5 w-5 text-muted-foreground" />
                ) : isActive ? (
                  <PlayCircle className="h-5 w-5" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-sm font-medium truncate",
                    isActive ? "" : "text-foreground"
                  )}
                >
                  {index + 1}. {module.title}
                </p>
                {module.description && (
                  <p
                    className={cn(
                      "text-xs truncate",
                      isActive ? "text-primary-foreground/70" : "text-muted-foreground"
                    )}
                  >
                    {module.description}
                  </p>
                )}
              </div>
            </button>
          );

          if (isCohortLocked) {
            return (
              <Tooltip key={module.id}>
                <TooltipTrigger asChild>
                  {button}
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Locked — awaiting instructor access</p>
                </TooltipContent>
              </Tooltip>
            );
          }

          return button;
        })}
      </TooltipProvider>
    </div>
  );
}
